import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { estagioHorarioService, estagioService } from '@/services/api';
import { PaginationParams, PaginationResponse, Estagio, EstagioHorario } from '@/types';
import { PlusIcon, ClockIcon, EyeIcon, CalendarIcon, UserIcon, BuildingOfficeIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export const HorariosPage: React.FC = () => {
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
  });
  const [searchText, setSearchText] = useState<string>("");
  const [deletingEstagioId, setDeletingEstagioId] = useState<number | null>(null);
  const { invalidateHorarios } = useQueryInvalidation();
  const { handleError } = useErrorHandler();
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  // Buscar estágios (estagiários) para mostrar informações relacionadas
  const { data: estagiosData, isLoading, error, refetch } = useQuery<PaginationResponse<Estagio>>({
    queryKey: ['estagios', filters],
    queryFn: () => estagioService.list(filters),
  });

  const horariosQueries = useQueries({
    queries: (estagiosData?.data ?? []).map((estagio) => ({
      queryKey: ['horarios', { estagioId: estagio.id }],
      queryFn: () => estagioHorarioService.getByEstagio(estagio.id),
    })),
  });
  const horariosError = horariosQueries.find((query) => query.error)?.error;
  const horariosByEstagio = new Map<number, EstagioHorario[]>(
    (estagiosData?.data ?? []).map((estagio, index) => [
      estagio.id,
      horariosQueries[index]?.data ?? [],
    ])
  );

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar estágios');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (horariosError) {
      const message = handleError(horariosError as any, 'Erro ao carregar horários');
      setLoadError(message);
    }
  }, [horariosError, handleError]);

  useEffect(() => {
    if (!error && !horariosError && estagiosData) {
      setLoadError(null);
    }
  }, [error, horariosError, estagiosData]);

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getHorariosPorEstagio = (estagioId: number) => {
    return horariosByEstagio.get(estagioId) ?? [];
  };

  const getDiaSemanaLabel = (dia: string) => {
    const dias = {
      'Segunda': 'Segunda-feira',
      'Terca': 'Terça-feira',
      'Quarta': 'Quarta-feira',
      'Quinta': 'Quinta-feira',
      'Sexta': 'Sexta-feira',
      'Sabado': 'Sábado',
      'Domingo': 'Domingo'
    };
    return dias[dia as keyof typeof dias] || dia;
  };

  const formatarHorario = (horario: string) => {
    // Se for um DateTime ISO, extrair apenas a parte do horário usando UTC
    if (horario.includes('T')) {
      const date = new Date(horario);
      return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
    }
    return horario;
  };

  const handleDeletarTodosHorarios = async (estagioId: number, alunoNome: string) => {
    showConfirm(
      'Excluir Todos os Horários',
      `Tem certeza que deseja deletar TODOS os horários do estagiário "${alunoNome}"?\n\nEsta ação não pode ser desfeita!`,
      async () => {
        setDeletingEstagioId(estagioId);

        try {
          // Usar o novo endpoint otimizado do backend
          const resultado = await estagioHorarioService.deleteByEstagio(estagioId);

          invalidateHorarios({ estagioId });

          toast.success(resultado.mensagem || 'Horários excluídos com sucesso!');
        } catch (error: unknown) {
          handleError(error as any, 'Erro ao deletar horários');
        } finally {
          setDeletingEstagioId(null);
        }
      },
      'danger'
    );
  };

  if (loadError) {
    return (
      <div className="text-center py-8 space-y-4">
        <p className="text-red-600 font-semibold">{loadError}</p>
        <Button
          onClick={() => {
            refetch();
            horariosQueries.forEach((query) => query.refetch());
          }}
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Estagiários e Horários</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os horários dos estagiários cadastrados
          </p>
        </div>
        <Link to="/horarios/novo" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto">
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Horário
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Estagiários</p>
                <p className="text-2xl font-bold text-gray-900">{estagiosData?.pagination?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Esta Página</p>
                <p className="text-2xl font-bold text-gray-900">{estagiosData?.data?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Páginas</p>
                <p className="text-2xl font-bold text-gray-900">{estagiosData?.pagination?.totalPages || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Estagiários */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Estagiários</CardTitle>
              <CardDescription>
                Gerencie os horários dos estagiários cadastrados no sistema
              </CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por aluno, empresa ou instituição"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : estagiosData?.data?.length === 0 ? (
            <div className="text-center py-8">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum estagiário encontrado</h3>
              <p className="text-gray-500 mb-4">Comece criando um novo estágio.</p>
              <Link to="/estagios/novo">
                <Button asChild>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Novo Estágio
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {estagiosData?.data?.map((estagio) => {
                const horarios = getHorariosPorEstagio(estagio.id);
                return (
                  <div
                    key={estagio.id}
                    className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex flex-col gap-4">
                      {/* Header e Botões */}
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <UserIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                                {estagio.aluno?.nome || 'Nome não encontrado'}
                              </h3>
                              <p className="text-sm text-gray-500">Estágio #{estagio.id}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="flex items-start gap-2">
                              <BuildingOfficeIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                              <div className="min-w-0">
                                <p className="text-gray-500">Empresa</p>
                                <p className="font-medium text-gray-900 truncate">
                                  {estagio.empresa?.nome || 'Empresa não encontrada'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                              <div className="min-w-0">
                                <p className="text-gray-500">Período</p>
                                <p className="font-medium text-gray-900 text-xs sm:text-sm">
                                  {new Date(estagio.dataInicio).toLocaleDateString('pt-BR')} - {new Date(estagio.dataTermino).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2 sm:flex-col sm:flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            title="Ver horários"
                            className="flex-1 sm:flex-initial text-blue-600 hover:text-blue-700 hover:border-blue-300"
                          >
                            <Link to={`/horarios/estagio/${estagio.id}`} className="flex items-center justify-center gap-1">
                              <EyeIcon className="h-4 w-4" />
                              <span className="sm:hidden">Ver</span>
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            title="Adicionar novo horário"
                            className="flex-1 sm:flex-initial text-green-600 hover:text-green-700 hover:border-green-300"
                          >
                            <Link to={`/horarios/novo?estagioId=${estagio.id}`} className="flex items-center justify-center gap-1">
                              <PlusIcon className="h-4 w-4" />
                              <span className="sm:hidden">Adicionar</span>
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletarTodosHorarios(estagio.id, estagio.aluno?.nome || 'Aluno')}
                            disabled={deletingEstagioId === estagio.id}
                            className="text-red-600 hover:text-red-700 hover:border-red-300 flex-1 sm:flex-initial"
                            title="Deletar todos os horários"
                          >
                            <div className="flex items-center justify-center gap-1">
                              {deletingEstagioId === estagio.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <>
                                  <TrashIcon className="h-4 w-4" />
                                  <span className="sm:hidden">Excluir</span>
                                </>
                              )}
                            </div>
                          </Button>
                        </div>
                      </div>

                      {/* Preview dos Horários */}
                      {horarios.length > 0 && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                            <ClockIcon className="h-4 w-4 mr-2" />
                            Horários Cadastrados ({horarios.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {horarios.slice(0, 6).map((horario) => (
                              <div key={horario.id} className="bg-white p-3 rounded border text-sm">
                                <div className="font-medium text-gray-900">
                                  {getDiaSemanaLabel(horario.diaSemana)}
                                </div>
                                <div className="text-gray-600">
                                  {formatarHorario(horario.horarioInicio)} - {formatarHorario(horario.horarioFim)}
                                </div>
                              </div>
                            ))}
                            {horarios.length > 6 && (
                              <div className="bg-white p-3 rounded border text-sm flex items-center justify-center text-gray-500">
                                +{horarios.length - 6} mais
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginação */}
          {estagiosData && estagiosData.pagination?.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={(filters.page || 1) <= 1}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 py-2 text-sm text-gray-700">
                  Página {filters.page || 1} de {estagiosData.pagination?.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={(filters.page || 1) >= (estagiosData.pagination?.totalPages || 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir Todos"
        cancelText="Cancelar"
        isLoading={deletingEstagioId !== null}
      />
    </div>
  );
};